import React, { useEffect, useRef, useState } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Box, CircularProgress, Table, TableBody } from '@material-ui/core'

const InfiniteScrollList = ({
  items,
  onLoadMore,
  renderRow,
  scrollbarClassName,
  isLoadingMore,
}) => {
  const hasScrolled = useRef(false)
  const pendingLoad = useRef(false)
  const displayedIds = useRef(new Set())
  const [favoriteIds, setFavoriteIds] = useState(() => new Set())
  const [displayItems, setDisplayItems] = useState([])

  useEffect(() => {
    hasScrolled.current = false
    pendingLoad.current = false

    if (items.length === 0) {
      displayedIds.current = new Set()
      setDisplayItems([])
      setFavoriteIds(new Set())
      return
    }

    const itemIds = new Set(items.map((item) => item.id))

    const isReset =
      displayedIds.current.size > 0 &&
      Array.from(displayedIds.current).some((id) => !itemIds.has(id))

    if (isReset) {
      displayedIds.current = new Set(items.map((item) => item.id))
      setFavoriteIds(
        new Set(items.filter((item) => item.favoriteId).map((item) => item.id)),
      )
      setDisplayItems(items)
      return
    }

    const newItems = items.filter((item) => !displayedIds.current.has(item.id))
    if (newItems.length === 0) return

    newItems.forEach((item) => displayedIds.current.add(item.id))

    setFavoriteIds((prev) => {
      const next = new Set(prev)
      newItems.forEach((item) => {
        if (item.favoriteId) next.add(item.id)
      })
      return next
    })

    setDisplayItems((prev) => [...prev, ...newItems])
  }, [items])

  const handleScrollY = () => {
    hasScrolled.current = true
  }

  const handleYReachEnd = () => {
    if (!hasScrolled.current || pendingLoad.current) return
    if (onLoadMore) {
      pendingLoad.current = true
      setTimeout(onLoadMore, 0)
    }
  }

  const handleFavoriteChange = (id, isFavorite) => {
    setFavoriteIds((prev) => {
      const next = new Set(prev)
      if (isFavorite) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })

    if (isFavorite) {
      setDisplayItems((prev) => {
        const item = prev.find((i) => i.id === id)
        if (!item) return prev
        return [item, ...prev.filter((i) => i.id !== id)]
      })
    }
  }

  return (
    <Box width="100%">
      <PerfectScrollbar
        className={scrollbarClassName}
        options={{
          wheelPropagation: true,
          suppressScrollX: true,
          suppressScrollY: false,
        }}
        onScrollY={handleScrollY}
        onYReachEnd={handleYReachEnd}
      >
        <Table
          width="100%"
          style={{ borderCollapse: 'separate', borderSpacing: '0 12px' }}
        >
          <TableBody>
            {displayItems.map((item) =>
              renderRow(item, favoriteIds.has(item.id), handleFavoriteChange),
            )}
          </TableBody>
        </Table>
        {isLoadingMore && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </PerfectScrollbar>
    </Box>
  )
}

export default InfiniteScrollList
